import { type ReactNode, useState } from 'react';
import { useConfig } from '../hooks/useConfig';
import { clearConsent } from '../lib/analytics';

const CONTACT_EMAIL = 'info@coffee214.com';
const LAST_UPDATED = '29 вересня 2026 року';

// Текст складено за тим, що сайт реально збирає й кому передає. Коли буде
// оформлено ФОП, у розділ «Хто ми» додаються ПІБ і РНОКПП власника даних.
const SECTIONS: ReadonlyArray<{ title: string; body: ReactNode }> = [
  {
    title: 'Хто ми',
    body: (
      <p>
        Інтернет-магазин кави 214° Coffee (coffee214.com) — власник і розпорядник персональних
        даних, які ви залишаєте на сайті. З будь-яких питань щодо ваших даних пишіть на <MailLink />
        .
      </p>
    ),
  },
  {
    title: 'Які дані ми збираємо',
    body: (
      <ul>
        <li>
          <strong>Акаунт:</strong> email, пароль (зберігається лише як незворотний хеш — сам пароль
          ми не знаємо), за бажанням — імʼя й телефон. Якщо ви входите через Google — email, імʼя та
          ідентифікатор Google.
        </li>
        <li>
          <strong>Доставка:</strong> прізвище, імʼя, по батькові, телефон отримувача, місто й
          відділення або адреса Нової Пошти, коментар до замовлення, за бажанням — дата народження в
          особистих даних.
        </li>
        <li>
          <strong>Замовлення та підписка:</strong> склад, суми, статус, номер ТТН.
        </li>
        <li>
          <strong>Оплата:</strong> даних картки ми не бачимо й не зберігаємо — оплата проходить на
          захищеній сторінці платіжного сервісу, ми отримуємо лише статус платежу.
        </li>
        <li>
          <strong>Telegram:</strong> якщо ви підключили нашого бота — ідентифікатор чату, щоб
          надсилати статуси замовлень.
        </li>
        <li>
          <strong>Звернення:</strong> імʼя, email, тема й текст, які ви надсилаєте через форму
          «Зворотній зв’язок».
        </li>
      </ul>
    ),
  },
  {
    title: 'Навіщо',
    body: (
      <ul>
        <li>щоб оформити, оплатити й доставити ваше замовлення;</li>
        <li>щоб повідомляти про статус замовлення листом і, якщо ви підключили, у Telegram;</li>
        <li>щоб ви могли входити в акаунт і бачити історію замовлень;</li>
        <li>щоб відповісти на ваше звернення;</li>
        <li>щоб розуміти, які сторінки відвідують, і покращувати сайт — лише за вашою згодою.</li>
      </ul>
    ),
  },
  {
    title: 'Кому ми передаємо дані',
    body: (
      <>
        <p>
          Лише тим сервісам, без яких магазин не працює, і лише ту частину даних, яка потрібна
          кожному з них. Ми не продаємо й не передаємо ваші дані для реклами.
        </p>
        <ul>
          <li>
            <strong>службі доставки</strong> — дані отримувача й адреса для доставки;
          </li>
          <li>
            <strong>платіжному сервісу</strong> — сума й номер замовлення для оплати;
          </li>
          <li>
            <strong>сервісу розсилки листів</strong> — ваш email і текст листа, щоб доставити
            сповіщення;
          </li>
          <li>
            <strong>месенджеру</strong> — текст сповіщення, якщо ви підключили нашого бота;
          </li>
          <li>
            <strong>сервісу авторизації</strong> — лише якщо ви обрали вхід через Google;
          </li>
          <li>
            <strong>сервісу веб-аналітики</strong> — лише якщо ви погодились у банері: які сторінки
            ви переглядаєте, приблизне місце й тип пристрою;
          </li>
          <li>
            <strong>хостинг-провайдеру</strong> — на його серверах працює сайт і зберігається база
            даних.
          </li>
        </ul>
        <p>
          Частина цих сервісів — іноземні компанії, тож ваші дані можуть оброблятися за межами
          України, зокрема в ЄС і США, із захистом, передбаченим їхніми правилами.
        </p>
      </>
    ),
  },
  {
    title: 'Скільки ми зберігаємо',
    body: (
      <p>
        Дані акаунта, особисті дані й історію замовлень — поки існує ваш акаунт. Звернення з форми
        на сайті не зберігаються: вони лише надходять нам листом. Дані Google Analytics (якщо ви
        дали згоду) зберігаються в Google не довше 14 місяців.
      </p>
    ),
  },
  {
    title: 'Ваші права',
    body: (
      <p>
        Ви можете дізнатися, які ваші дані ми зберігаємо, виправити їх, попросити видалити акаунт
        разом з даними або відкликати згоду на обробку. Для цього напишіть на <MailLink /> — ми
        відповімо протягом 30 днів. Прив’язку Telegram можна вимкнути самостійно, надіславши боту
        /stop.
      </p>
    ),
  },
  {
    title: 'Аналітика та cookie',
    body: (
      <>
        <p>
          Службова cookie потрібна, щоб ви лишались у своєму акаунті. Кошик і обране зберігаються у
          вашому браузері й нікуди не надсилаються, доки ви не оформите замовлення.
        </p>
        <p>
          Якщо ви погодились у банері, ми використовуємо Google Analytics: він ставить свої cookie й
          рахує, які сторінки переглядають, з якого приблизно міста й пристрою. Імен, контактів і
          даних замовлень ми туди не передаємо. Без вашої згоди Google Analytics на сайті не
          завантажується. Рекламних трекерів на сайті немає.
        </p>
        <ConsentResetButton />
      </>
    ),
  },
  {
    title: 'Зміни політики',
    body: (
      <p>Якщо ми змінимо цю політику, нова версія зʼявиться на цій сторінці з оновленою датою.</p>
    ),
  },
];

function MailLink() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent no-underline">
      {CONTACT_EMAIL}
    </a>
  );
}

// Відкликати чи змінити згоду можна будь-коли — вікно згоди зʼявиться знову на наступній сторінці.
function ConsentResetButton() {
  const { data: config } = useConfig();
  const [done, setDone] = useState(false);
  if (!config?.gaMeasurementId) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          clearConsent();
          setDone(true);
        }}
        className="cursor-pointer border border-stroke bg-transparent px-[14px] py-[9px] font-mono text-[12px] font-bold text-ink"
      >
        Змінити налаштування cookie
      </button>
      {done && <p className="mt-3">Готово. Ми запитаємо вашу згоду знову на наступній сторінці.</p>}
    </>
  );
}

export function PrivacyPage() {
  return (
    <div className="section-pad">
      <h1 className="m-0 mb-3 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
        Політика конфіденційності
      </h1>
      <p className="mono-label mb-10">Останнє оновлення: {LAST_UPDATED}</p>

      <div className="max-w-[820px] space-y-10 border border-line bg-surface p-[clamp(20px,4vw,48px)]">
        {SECTIONS.map((section, index) => (
          <section key={section.title}>
            <h2 className="m-0 mb-3 text-[17px] font-bold uppercase tracking-[0.02em] text-ink">
              <span className="text-accent">{index + 1}.</span> {section.title}
            </h2>
            <div className="space-y-3 text-[15px] leading-relaxed text-muted [&_li]:mb-2 [&_strong]:text-ink [&_ul]:m-0 [&_ul]:pl-5">
              {section.body}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
